/**
 * The one place a diagnostic `.txt` is built.
 *
 * There used to be two near-duplicate builders — one in DebugService.download,
 * one in the Debug tab — and which of them ran depended only on whether the
 * call was still cached. They had already drifted: the service copy silently
 * omitted the EVENTS section, so the same call exported differently before and
 * after a page reload. Everything now goes through here.
 */

import type { CallDebugInfo, SoftphoneEvent } from './index.types';

/**
 * How a direction reads in the report. A call lifecycle event has none — it was
 * neither sent nor received — and marking it with an arrow would invent traffic.
 */
const DIRECTION_MARK = {
  incoming: '←',
  outgoing: '→',
  none: '·',
} as const;

const HEAVY_RULE = '='.repeat(43);
const LIGHT_RULE = '-'.repeat(43);

/**
 * Lines that are secrets and carry no diagnostic value once the call has
 * ended. They are blanked rather than deleted, so a reader can still see the
 * SDP's real structure and tell that something was deliberately removed.
 */
const REDACTED_SDP_ATTRIBUTES = ['ice-ufrag', 'ice-pwd', 'fingerprint'];

const CONFIDENTIALITY_NOTICE = [
  'CONFIDENTIAL — contains personal data (phone numbers) and network',
  'information (IP addresses). Share only through approved support channels.',
  'Session credentials have been redacted from the SDP.',
].join('\n');

/**
 * Blank the value of every credential attribute in an SDP body.
 */
export const redactSdp = (sdp: string | null): string | null => {
  if (!sdp) return sdp;

  return sdp
    .split('\n')
    .map((line) => {
      const attribute = REDACTED_SDP_ATTRIBUTES.find((name) =>
        line.startsWith(`a=${name}:`)
      );
      if (!attribute) return line;
      // Preserve a trailing \r so CRLF-terminated SDP stays CRLF-terminated.
      return line.endsWith('\r')
        ? `a=${attribute}:[redacted]\r`
        : `a=${attribute}:[redacted]`;
    })
    .join('\n');
};

const section = (title: string, body: string): string =>
  `\n${LIGHT_RULE}\n${title}\n${LIGHT_RULE}\n${body}\n`;

const formatDeviceChanges = (changes: SoftphoneEvent[] | undefined): string => {
  if (!changes || changes.length === 0) {
    return 'No device changes during this call';
  }
  return changes
    .map(
      (change, index) =>
        `${index + 1}. [${new Date(
          change.timestamp
        ).toLocaleTimeString()}] ${change.message}`
    )
    .join('\n');
};

/**
 * An event's payload, with the traced SIP message pulled out of the JSON.
 *
 * A SIP message is several lines, and inside `JSON.stringify` it collapses into
 * one line of `\r\n` escapes — present in the file, but not readable, which
 * for the one artefact support actually opens is the same as missing.
 */
const formatEventDetails = (
  details: Record<string, unknown> | undefined
): string => {
  if (!details) {
    return '';
  }

  const { raw, ...rest } = details;
  const lines: string[] = [];

  if (Object.keys(rest).length > 0) {
    lines.push(`   Details: ${JSON.stringify(rest, null, 2)}`);
  }

  if (typeof raw === 'string' && raw.length > 0) {
    lines.push(
      '   Message:',
      ...raw.split(/\r?\n/).map((line) => `     ${line}`)
    );
  }

  return lines.join('\n');
};

const formatEvents = (events: SoftphoneEvent[] | undefined): string => {
  if (!events || events.length === 0) {
    return 'No events captured';
  }
  return events
    .map(
      (event, index) => `
${index + 1}. [${event.type}] ${DIRECTION_MARK[event.direction ?? 'none']} ${
        event.message
      }
   Time: ${new Date(event.timestamp).toLocaleString()}
${formatEventDetails(event.details)}`
    )
    .join('\n');
};

/**
 * The call header. Scenario lines only appear when there is something to say,
 * so a plain 1:1 call keeps exactly the header it had before transfer and
 * conference capture existed.
 */
const formatCallHeader = (info: CallDebugInfo): string => {
  const lines = [
    `Call ID: ${info.callId}`,
    `Phone Number: ${info.phoneNumber}`,
    `Direction: ${info.direction}`,
    `State: ${info.state}`,
  ];

  if (info.scenario && info.scenario !== 'direct') {
    lines.push(`Scenario: ${info.scenario}`);
  }
  if (info.endedBy) {
    lines.push(`Ended By: ${info.endedBy}`);
  }
  if (info.interactionId && info.interactionId !== info.callId) {
    lines.push(`Interaction ID: ${info.interactionId}`);
  }
  if (info.relatedCallIds && info.relatedCallIds.length > 0) {
    lines.push(`Related Calls: ${info.relatedCallIds.join(', ')}`);
  }

  lines.push(
    `Start Time: ${info.startTime}`,
    `Answer Time: ${info.answerTime || 'N/A'}`,
    `End Time: ${info.endTime || 'N/A'}`
  );

  return lines.join('\n');
};

const formatTransferSection = (info: CallDebugInfo): string => {
  const transfer = info.transfer;
  if (!transfer) return '';

  const lines = [
    `Type: ${transfer.type}`,
    `Destination: ${transfer.destination ?? 'N/A'}`,
    `Outcome: ${transfer.outcome}`,
  ];

  if (transfer.outcome === 'referred') {
    lines.push(
      'Note: the REFER was sent and the far end reported nothing back.',
      '      Success was not observed, only attempted.'
    );
  }
  if (transfer.relatedCallId) {
    lines.push(`Other Leg: ${transfer.relatedCallId}`);
  }
  if (transfer.referResponses && transfer.referResponses.length > 0) {
    lines.push('REFER Responses:', ...transfer.referResponses.map((r) => `  ${r}`));
  }
  if (transfer.failureReason) {
    lines.push(`Failure Reason: ${transfer.failureReason}`);
  }

  return section('TRANSFER', lines.join('\n'));
};

const formatConferenceSection = (info: CallDebugInfo): string => {
  const conference = info.conference;
  if (!conference) return '';

  const participants = conference.participantIds ?? [];
  const lines = [
    `Room GUID: ${conference.roomGuid}`,
    `Room URI: ${conference.roomUri ?? 'N/A'}`,
    `Participants (${participants.length}):`,
    participants.length > 0
      ? participants.map((id) => `  ${id}`).join('\n')
      : '  None recorded',
    '',
    'Note: participants are tracked locally by the client. The conference',
    '      server emits no join/leave events, so this list is inferred and',
    '      may not match the room exactly.',
  ];

  if (conference.rolledBack) {
    lines.push('', 'This merge failed and was rolled back.');
  }

  return section('CONFERENCE', lines.join('\n'));
};

/**
 * What the quality monitor concluded about the audio.
 *
 * Units are converted here rather than stored converted: `getStats` reports
 * jitter and round-trip in seconds and bitrate in bits, and a report that
 * prints `0.004` next to `ms` is how a 4ms call gets read as a 4-second one.
 *
 * Absent is stated rather than skipped. The monitor watches the current call
 * only and stops for conference participants once their peer connections close
 * after the REFER, so a missing block means "not measured" — and a reader who
 * assumes it means "nothing to report" reaches the opposite conclusion.
 */
const formatQualitySection = (info: CallDebugInfo): string => {
  const quality = info.quality;

  if (!quality) {
    return section(
      'CALL QUALITY',
      [
        'Not measured for this leg.',
        '',
        'Quality monitoring follows the current call and stops for conference',
        'participants once their own peer connections are closed. No figures',
        'here means none were collected, not that none were worth reporting.',
      ].join('\n')
    );
  }

  const lines: string[] = [];

  if (quality.mosScore !== undefined) {
    // The scale is not obvious from the number, and 3.6 is the line below
    // which people start saying "the call was bad" without knowing why.
    lines.push(
      `MOS Score: ${quality.mosScore.toFixed(2)} (1.0 unusable - 5.0 perfect, ITU-T G.107)`
    );
  }

  if (quality.averageJitter !== undefined) {
    lines.push(
      `Average Jitter: ${Math.round(quality.averageJitter * 1000)} ms`
    );
  }

  if (quality.totalPacketsLost !== undefined) {
    lines.push(`Packets Lost (inbound, total): ${quality.totalPacketsLost}`);
  }

  if (quality.averageRoundTripTime !== undefined) {
    lines.push(
      `Average Round Trip: ${Math.round(quality.averageRoundTripTime * 1000)} ms`
    );
  }

  if (quality.availableBitrate !== undefined) {
    lines.push(
      `Available Outgoing Bitrate: ${Math.round(quality.availableBitrate / 1000)} kbps (capacity, not throughput)`
    );
  }

  if (quality.codec) {
    const { name, clockRate, channels } = quality.codec;
    const detail = [clockRate, channels].filter(Boolean).join('/');

    lines.push(`Codec: ${detail ? `${name}/${detail}` : name}`);
  }

  // How much the averages are worth. Two samples and sixty average the same
  // way and mean very different things.
  lines.push(
    `Samples: ${quality.sampleCount} (collected every 2s while the call ran)`
  );

  return section('CALL QUALITY', lines.join('\n'));
};

/**
 * Build the report body for a single call.
 */
export const buildCallReport = (info: CallDebugInfo): string =>
  [
    formatCallHeader(info),
    formatTransferSection(info),
    formatConferenceSection(info),
    formatQualitySection(info),
    section(
      'AUDIO DEVICES USED',
      [
        `Microphone: ${info.devices?.microphone ?? 'N/A'}`,
        `Speaker: ${info.devices?.speaker ?? 'N/A'}`,
        `Ringer: ${info.devices?.ringer ?? 'N/A'}`,
      ].join('\n')
    ),
    section(
      `DEVICE CHANGE TIMELINE (${info.deviceChanges?.length || 0} total)`,
      formatDeviceChanges(info.deviceChanges)
    ),
    section(
      'WEBRTC CONNECTION STATE',
      [
        `ICE Connection State: ${info.iceConnectionState || 'N/A'}`,
        `Connection State: ${info.connectionState || 'N/A'}`,
        `Signaling State: ${info.signalingState || 'N/A'}`,
      ].join('\n')
    ),
    section(
      'ICE CANDIDATES SUMMARY',
      [
        `Local Candidates: ${info.iceCandidates?.local.length || 0}`,
        `Remote Candidates: ${info.iceCandidates?.remote.length || 0}`,
        '',
        'Local Candidates:',
        info.iceCandidates?.local.join('\n') || 'None',
        '',
        'Remote Candidates:',
        info.iceCandidates?.remote.join('\n') || 'None',
      ].join('\n')
    ),
    section(
      'LOCAL SDP (OFFER/ANSWER)',
      redactSdp(info.localSdp) || 'No local SDP available'
    ),
    section(
      'REMOTE SDP (OFFER/ANSWER)',
      redactSdp(info.remoteSdp) || 'No remote SDP available'
    ),
    section(`EVENTS (${info.events?.length || 0} total)`, formatEvents(info.events)),
  ].join('');

const footer = (): string =>
  `${HEAVY_RULE}
Generated: ${new Date().toISOString()}
User Agent: ${navigator.userAgent}
${HEAVY_RULE}
`;

/**
 * The complete file for one call.
 */
export const buildDebugReport = (info: CallDebugInfo): string => `
${HEAVY_RULE}
SOFTPHONE DEBUG INFORMATION
${HEAVY_RULE}

${CONFIDENTIALITY_NOTICE}

${buildCallReport(info)}
${footer()}`;

/**
 * The complete file for a whole interaction — both legs of a warm transfer,
 * every leg of a conference plus its room.
 *
 * A multi-leg scenario is not diagnosable one leg at a time: the question is
 * almost always what one leg was doing while another leg did something else.
 */
const buildInteractionBody = (legs: CallDebugInfo[]): string => {
  const [first] = legs;
  const scenario = legs.find((l) => l.scenario && l.scenario !== 'direct')
    ?.scenario;

  const overview = [
    `Interaction ID: ${first?.interactionId ?? first?.callId ?? 'N/A'}`,
    `Scenario: ${scenario ?? 'direct'}`,
    `Legs (${legs.length}):`,
    ...legs.map(
      (leg, index) =>
        `  ${index + 1}. ${leg.callId} — ${leg.phoneNumber} (${leg.direction}, ${
          leg.scenario ?? 'direct'
        })`
    ),
  ].join('\n');

  const body = legs
    .map(
      (leg, index) => `
${HEAVY_RULE}
LEG ${index + 1} OF ${legs.length}
${HEAVY_RULE}
${buildCallReport(leg)}`
    )
    .join('\n');

  return `${overview}
${body}`;
};

export const buildInteractionReport = (legs: CallDebugInfo[]): string => `
${HEAVY_RULE}
SOFTPHONE DEBUG INFORMATION — INTERACTION
${HEAVY_RULE}

${CONFIDENTIALITY_NOTICE}

${buildInteractionBody(legs)}
${footer()}`;

/**
 * The whole history in one file, interaction by interaction.
 *
 * Deliberately not a flat list of legs: a session that contains a warm transfer
 * and two direct calls is three things that happened, and flattening it would
 * throw away the grouping the panel exists to make visible. Each group reuses
 * the interaction body, so a leg reads identically in every export it appears in.
 */
export const buildHistoryReport = (groups: CallDebugInfo[][]): string => {
  const legCount = groups.reduce((total, legs) => total + legs.length, 0);

  const body = groups
    .map(
      (legs, index) => `
${HEAVY_RULE}
INTERACTION ${index + 1} OF ${groups.length}
${HEAVY_RULE}
${buildInteractionBody(legs)}`
    )
    .join('\n');

  return `
${HEAVY_RULE}
SOFTPHONE DEBUG INFORMATION — HISTORY
${HEAVY_RULE}

${CONFIDENTIALITY_NOTICE}

Interactions: ${groups.length}
Records: ${legCount}
${body}
${footer()}`;
};

const safeTimestamp = (): string =>
  new Date().toISOString().replace(/[:.]/g, '-');

export const buildDebugFilename = (info: CallDebugInfo): string =>
  `softphone-debug-${String(info.phoneNumber).replace(
    /[^0-9]/g,
    ''
  )}-${safeTimestamp()}.txt`;

export const buildHistoryFilename = (): string =>
  `softphone-debug-history-${safeTimestamp()}.txt`;

export const buildInteractionFilename = (legs: CallDebugInfo[]): string => {
  const [first] = legs;
  const id = String(first?.interactionId ?? first?.callId ?? 'unknown').replace(
    /[^A-Za-z0-9-]/g,
    ''
  );
  return `softphone-debug-interaction-${id}-${safeTimestamp()}.txt`;
};

/**
 * Hand the file to the browser. Kept here so both download paths deliver it
 * the same way.
 */
export const downloadTextFile = (filename: string, content: string): void => {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
